import { useState, useEffect, useCallback } from "react";
import Constants from "expo-constants";
import { useFeatureFlags } from "@src/services/feature-flags/feature-flags";
import { isVersionLessThan } from "@src/utils/version-comparison";

interface ForceUpgradeConfig {
  minimumVersion?: string;
  upgradeMessage?: string;
  appStoreUrl?: string;
  playStoreUrl?: string;
}

export const useForceUpgrade = () => {
  const [showForceUpgrade, setShowForceUpgrade] = useState(false);
  const [forceUpgradeConfig, setForceUpgradeConfig] = useState<{
    upgradeMessage?: string;
    appStoreUrl?: string;
    playStoreUrl?: string;
  }>({});

  const { isInitialized, getFlagPayload } = useFeatureFlags();

  const checkForceUpgrade = useCallback((): void => {
    try {
      // Get the force upgrade config payload from feature flags
      const upgradeConfig = getFlagPayload<ForceUpgradeConfig>("force_upgrade_config");

      // Validate that we have a proper config object
      if (!upgradeConfig || typeof upgradeConfig !== "object") {
        setShowForceUpgrade(false);
        return;
      }

      if (upgradeConfig && upgradeConfig.minimumVersion) {
        const currentVersion = Constants.expoConfig?.version || "1.0.0";
        const needsUpgrade = isVersionLessThan(currentVersion, upgradeConfig.minimumVersion);

        if (needsUpgrade) {
          setForceUpgradeConfig({
            upgradeMessage: upgradeConfig.upgradeMessage,
            appStoreUrl: upgradeConfig.appStoreUrl,
            playStoreUrl: upgradeConfig.playStoreUrl,
          });
          setShowForceUpgrade(true);
        } else {
          // Reset if no upgrade needed
          setShowForceUpgrade(false);
        }
      } else {
        // Reset if no config
        setShowForceUpgrade(false);
      }
    } catch (error) {
      console.error("[Force Upgrade] Error checking for upgrade:", error);
    }
  }, [getFlagPayload]);

  // Check for force upgrade when flags change or initialization status changes
  useEffect(() => {
    if (isInitialized) {
      checkForceUpgrade();
    }
  }, [isInitialized, checkForceUpgrade]);

  return {
    showForceUpgrade,
    forceUpgradeConfig,
    checkForceUpgrade,
  };
};
